import { fetchChatList, fetchMessageList, FetchChatListResponse, FetchMessageListResponse } from './api'
import axios from './axios'
import { Singleflight } from '@voghion/fe-util'
import type { ClientInfo } from '@voghion/fe-util'
import { ContentMessageToSend, Conversation, Message } from '@/utils/constant'

/**
 * 入口信息
 */
export type EntryInfo = {
  /**
   * 页面来源
   */
  pageFrom?: string
  /**
   * 目标 id
   */
  toCombineId?: number
  /**
   * 目标角色
   */
  toUserRole?: string
  /**
   * 目标类型
   */
  toUserType?: string
}

export type IMUserInfo = {
  /**
   * 用户 id
   */
  imUserId: number
  /**
   * 用户类型
   */
  imUserType: string
  /**
   * 昵称
   */
  nickname: string
}

export type VipUserInfo = {
  /**
   * 头像
   */
  headImage: string
  /**
   * 昵称
   */
  nickname: string
}

export type ListFetchOptions = {
  /**
   * 页码
   */
  page?: number
  /**
   * 是否强制刷新，刷新时页码强制设为 1
   */
  forceRefresh?: boolean
  /**
   * 是否是加载更多
   */
  loadMore?: boolean
}

export interface MessageFetchOptions extends ListFetchOptions {
  /**
   * 会话标识
   */
  conversationId: number
  /**
   * 最后一条消息 id
   */
  startMessageId?: number
}

type ListCache<T> = {
  /**
   * 记录数组
   */
  items: T[]

  /**
   * 更新时间戳
   */
  updateTime: number
}

interface ConversationListCache extends ListCache<Conversation> {
  /**
   * 当前页码
   */
  page: number
  /**
   * 总数
   */
  totalCount: number
}

interface MessageListCache extends ListCache<Message | ContentMessageToSend> {
  /**
   * 会话id
   */
  conversationId: number
}

const singleflight = Singleflight.getInstance()
const chatList = 'chats'
const messageListPrefix = 'messages_'

/**
 * worker 数据源，缓存设备信息，接口数据等
 */
export class Store {
  /**
   * 平台客服信息
   */
  public vipUserInfo: VipUserInfo

  /**
   * 设备信息
   */
  private _clientInfo: ClientInfo

  /**
   * 入口信息
   */
  private _entryInfo: EntryInfo

  /**
   * im 用户信息
   */
  public imUserInfo: IMUserInfo

  /**
   * 会话分页数
   */
  public chatPageSize = 100

  /**
   * 消息分页数
   */
  public messagePageSize = 20

  /**
   * 数据缓存
   */
  private listCache = new Map<string, ConversationListCache | MessageListCache>()

  set clientInfo(value: ClientInfo) {
    this._clientInfo = value
    if (value) {
      const clientInfoVOObj = {
        ...value
      }
      // 处理非 non ISO-8859-1
      const keys = ['mediaSource', 'afChannel', 'afAdset', 'campaign']
      for (const key of keys) {
        if (clientInfoVOObj[key]) {
          clientInfoVOObj[key] = encodeURIComponent(clientInfoVOObj[key])
        }
      }
      axios.clientInfoStr = JSON.stringify(clientInfoVOObj)
    } else {
      axios.clientInfoStr = ''
    }
  }

  get clientInfo() {
    return this._clientInfo
  }

  set entryInfo(value: EntryInfo) {
    this._entryInfo = value
  }

  get entryInfo() {
    return this._entryInfo
  }

  /**
   * 获取会话列表
   */
  async getChatList(options: ListFetchOptions = {}) {
    if (!this.clientInfo) {
      return {
        data: null,
        error: new Error('no client info')
      }
    } else if (!this.imUserInfo) {
      return {
        data: null,
        error: new Error('no im user info')
      }
    }
    const cache = this.listCache.get(chatList) as ConversationListCache
    let page = options.page || 1
    if (page < 1 || options.forceRefresh) {
      page = 1
    }
    const pageSize = this.chatPageSize
    if (cache) {
      const start = (page - 1) * pageSize
      const end = start + pageSize
      if (!options.forceRefresh && cache.page >= page) {
        const items = cache.items.slice(start, end)
        return {
          data: {
            items
          },
          error: null
        }
      }
    }
    const finalParams = {
      pageSize: pageSize,
      pageIndex: page,
      imUserId: this.imUserInfo.imUserId,
      voghionToken: this.clientInfo.userToken,
      userType: 'CUSTOMER'
    }
    try {
      const res = await singleflight.do<FetchChatListResponse>(`fetch_chat_list_${JSON.stringify(finalParams)}`, () => {
        return fetchChatList(finalParams)
      })
      if (res.success) {
        const data = res.data
        const totalCount = data.totalCount
        const conversations = data.conversations || []
        const items = page === 1 ? conversations : (cache ? cache.items : []).concat(conversations)
        this.listCache.set(chatList, {
          page,
          items,
          totalCount: totalCount,
          updateTime: Date.now()
        })
        return {
          data: {
            items: conversations
          },
          error: null
        }
      } else {
        return {
          data: null,
          error: new Error(res.rtn_msg)
        }
      }
    } catch (error) {
      console.log(error)
      return {
        data: null,
        error
      }
    }
  }

  /**
   * 获取消息列表
   * @param page
   * @param options
   */
  async getMessageList(options: MessageFetchOptions) {
    if (!this.clientInfo) {
      return {
        data: null,
        error: new Error('no client info')
      }
    } else if (!this.imUserInfo) {
      return {
        data: null,
        error: new Error('no im user info')
      }
    }
    const cacheKey = `${messageListPrefix}${options.conversationId}`
    const startMessageId = options.startMessageId || ''
    const cache = this.listCache.get(cacheKey) as MessageListCache
    const pageSize = this.messagePageSize
    if (cache) {
      const items = cache.items
      const startMessageIndex = Math.max(
        !startMessageId
          ? 0
          : items.findIndex((value) => {
              return value.messageId === startMessageId
            }),
        0
      )
      if (items.length - startMessageIndex >= pageSize) {
        const messages = cache.items.slice(startMessageIndex, startMessageIndex + pageSize)
        return {
          data: {
            items: messages
          },
          error: null
        }
      }
    }
    const finalParams = {
      limit: 20,
      startMessageId: startMessageId,
      conversationId: options.conversationId,
      voghionToken: this.clientInfo.userToken,
      userType: 'CUSTOMER'
    }
    try {
      const res = await singleflight.do<FetchMessageListResponse>(
        `fetch_message_list_${JSON.stringify(finalParams)}`,
        () => {
          return fetchMessageList(finalParams)
        }
      )
      if (res.success) {
        const data = res.data
        const messages = data.messages || []
        const cache = this.listCache.get(cacheKey) as MessageListCache
        const oldMessages = cache ? cache.items : []
        const oldMessageIds = oldMessages.reduce((list, message) => {
          list.push(message.messageId)
          return list
        }, [])
        const newMessages = []
        for (const message of messages) {
          if (!oldMessageIds.includes(message.messageId)) {
            oldMessageIds.push(message.messageId)
            oldMessages.push(message)
            newMessages.push(message)
          }
        }
        this.listCache.set(cacheKey, {
          conversationId: options.conversationId,
          items: oldMessages,
          updateTime: Date.now()
        })
        return {
          data: {
            items: newMessages
          },
          error: null
        }
      } else {
        return {
          data: null,
          error: new Error(res.rtn_msg)
        }
      }
    } catch (error) {
      console.log(error)
      return {
        data: null,
        error
      }
    }
  }

  /**
   * 根据标识获取会话
   * @param conversationId
   * @returns
   */
  getChatById(conversationId: number) {
    const cache = this.listCache.get(chatList) as ConversationListCache
    if (cache) {
      return cache.items.find((r) => r.conversationId === conversationId)
    }
    return null
  }

  /**
   * 新增会话
   * @param conversation
   */
  addNewChat(conversation: Conversation) {
    const cache = this.listCache.get(chatList) as ConversationListCache
    if (cache) {
      const items = cache.items
      items.unshift(conversation)
    } else {
      this.listCache.set(chatList, {
        page: 0,
        items: [conversation],
        totalCount: 100,
        updateTime: Date.now()
      })
    }
  }

  /**
   * 新增消息
   * @param message
   */
  addOrUpdateNewMessage(message: ContentMessageToSend) {
    const cacheKey = `${messageListPrefix}${message.conversationId}`
    const cache = this.listCache.get(cacheKey) as MessageListCache
    if (cache) {
      const items = cache.items || []
      const messageExists = items.find(
        (r) => r.messageId === message.messageId || ('applyId' in r && r.applyId === message.applyId)
      )
      if (messageExists) {
        Object.assign(messageExists, message)
      } else {
        items.unshift(message)
      }
    } else {
      this.listCache.set(cacheKey, {
        conversationId: message.conversationId,
        items: [message],
        updateTime: 0
      })
    }
  }

  /**
   * 清空数据
   */
  clear() {
    this.listCache.clear()
    this.clientInfo = undefined
    this.imUserInfo = undefined
  }
}
